import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, Sparkles, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SafeImage from '../components/SafeImage';
import OfferRibbon from '../components/OfferRibbon';
import PriceTag from '../components/PriceTag';
import WhatsAppShareBtn from '../components/WhatsAppShareBtn';
import { usePackages } from '../store';

const isOnOffer = (p) => !!p.offer_enabled && Number(p.offer_price) > 0 && Number(p.offer_price) < Number(p.price || 0);

const OffersPage = () => {
  const { packages } = usePackages();

  const offers = useMemo(() => {
    return (packages || [])
      .filter((p) => p.published !== false)
      .filter(isOnOffer);
  }, [packages]);

  // SEO
  React.useEffect(() => {
    document.title = 'Deals & Offers on Holiday Packages | Travelsera';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', 'Browse every Travelsera holiday package currently on sale. Limited-time deals across India and international destinations, with live countdowns and discounted prices.');
    return () => { document.title = 'Travelsera - Journeys, Redefined'; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="offers-page">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-[#0d64a8] via-[#0b5591] to-[#083f6d] text-white overflow-hidden">
        <Header />
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 pt-28 pb-14 relative">
          <Link to="/" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm mb-4">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1 bg-rose-500/90 text-white text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded-md">
              <Sparkles size={12} /> Live Deals
            </span>
            <span className="text-white/80 text-xs">Updated in real time</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Special Offers &amp; Deals</h1>
          <p className="mt-3 text-white/90 max-w-xl">
            {offers.length > 0
              ? `${offers.length} holiday${offers.length === 1 ? '' : 's'} currently on sale — grab them before the timers run out.`
              : 'No live offers right now. Check back soon — our travel curators refresh deals every week.'}
          </p>
        </div>
        {/* soft decorative overlay */}
        <div className="pointer-events-none absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#ffcc29]/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
      </div>

      {/* Body */}
      <section className="py-12 bg-[#f7fafc]">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
          {offers.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl py-20 text-center">
              <Tag className="mx-auto text-gray-300 mb-3" size={48} />
              <h2 className="text-xl font-semibold text-gray-800">No live deals yet</h2>
              <p className="text-sm text-gray-500 mt-2">Explore our handpicked packages while we cook up fresh offers.</p>
              <Link to="/" className="inline-flex items-center gap-1.5 mt-6 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-5 py-2.5 rounded-md">
                Browse packages
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.map((p) => (
                <div
                  key={p.pkg_code || p.id}
                  data-testid={`offer-card-${p.pkg_code || p.id}`}
                  className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  <Link to={`/package/${p.pkg_code || p.id}`} className="block group">
                    <div className="relative">
                      <SafeImage src={p.image} alt={p.title} fallbackType={p.fbType || 'default'} className="w-full h-52 object-cover" />
                      <OfferRibbon text={p.offer_ribbon_text || 'Limited Offer'} endsAt={p.offer_ends_at} />
                      {(() => {
                        const themeRaw = Array.isArray(p.themes) && p.themes.length > 0
                          ? p.themes[0]
                          : (p.category === 'india' ? 'India' : 'International');
                        const label = String(themeRaw).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                        return (
                          <div className="absolute top-3 right-3 bg-[#0d64a8] text-white text-[11px] font-medium px-2.5 py-1 rounded shadow-sm capitalize">
                            {label}
                          </div>
                        );
                      })()}
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white border border-gray-100 shadow px-4 py-2 rounded-md flex items-center gap-1.5 text-sm text-gray-700 whitespace-nowrap">
                        <Clock size={14} className="text-[#0d64a8]" /> {p.duration}
                      </div>
                    </div>
                    <div className="pt-8 px-5 pb-3">
                      <h3 className="text-[17px] font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[48px] group-hover:text-[#0d64a8] transition">{p.title}</h3>
                      <div className="flex items-start gap-1.5 text-sm text-gray-600 mb-3">
                        <MapPin size={14} className="text-[#0d64a8] mt-0.5 shrink-0" />
                        <span className="line-clamp-1">{p.itinerary}</span>
                      </div>
                      <div className="h-px bg-gray-100 my-3" />
                      <PriceTag
                        price={p.price}
                        offerEnabled={p.offer_enabled}
                        offerPrice={p.offer_price}
                        size="md"
                        suffix="/ Adult"
                      />
                    </div>
                  </Link>
                  <div className="px-5 pb-5 flex items-center gap-2">
                    <Link
                      to={`/package/${p.pkg_code || p.id}`}
                      className="flex-1 text-center bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm font-medium px-3 py-2 rounded-md transition"
                    >
                      View Details
                    </Link>
                    <WhatsAppShareBtn pkg={p} variant="card" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OffersPage;
